import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { useUpdateTcRunRatings } from '@/queries/useTcRun';
import type { TcRunAppraiseeResponse, AppraisalSkillAreaRating, TcRunSkillAreaRatingItem } from '@/api/types/tcRun.types';
import { useTcRunAppraiseeMutationQueue } from './TcRunAppraiseeMutationQueueContext';

interface RatingsAndActionsSectionProps {
  tcId: number;
  appraisee: TcRunAppraiseeResponse;
  readOnly: boolean;
}

const RATING_OPTIONS: { value: AppraisalSkillAreaRating; label: string }[] = [
  { value: 'BT', label: 'BT' },
  { value: 'OT', label: 'OT' },
  { value: 'AT', label: 'AT' },
  { value: 'O', label: 'O' },
];

export function RatingsAndActionsSection({ tcId, appraisee, readOnly }: RatingsAndActionsSectionProps) {
  const { runSerialized } = useTcRunAppraiseeMutationQueue();
  const updateRatingsMutation = useUpdateTcRunRatings();

  // Local state for skill area ratings
  const [localRatings, setLocalRatings] = useState<Record<number, AppraisalSkillAreaRating | null>>(() => {
    const initial: Record<number, AppraisalSkillAreaRating | null> = {};
    for (const sa of appraisee.skillAreaFeedback) {
      initial[sa.skillAreaId] = sa.updatedRating ?? sa.rating;
    }
    return initial;
  });
  const [localFinalRating, setLocalFinalRating] = useState<AppraisalSkillAreaRating | null>(appraisee.finalRating);
  const [localPromote, setLocalPromote] = useState(appraisee.promote);
  const [localHoldAtGrade, setLocalHoldAtGrade] = useState(appraisee.holdAtGrade);

  // serverKey/prevKey pattern for cache-aware local state resets
  const serverKey = useMemo(
    () =>
      JSON.stringify({
        feedback: appraisee.skillAreaFeedback.map((sa) => ({
          id: sa.skillAreaId,
          rating: sa.updatedRating,
        })),
        finalRating: appraisee.finalRating,
        promote: appraisee.promote,
        holdAtGrade: appraisee.holdAtGrade,
      }),
    [appraisee.skillAreaFeedback, appraisee.finalRating, appraisee.promote, appraisee.holdAtGrade]
  );

  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    const resetRatings: Record<number, AppraisalSkillAreaRating | null> = {};
    for (const sa of appraisee.skillAreaFeedback) {
      resetRatings[sa.skillAreaId] = sa.updatedRating ?? sa.rating;
    }
    setLocalRatings(resetRatings);
    setLocalFinalRating(appraisee.finalRating);
    setLocalPromote(appraisee.promote);
    setLocalHoldAtGrade(appraisee.holdAtGrade);
  }

  const buildSkillAreaRatings = (ratings: Record<number, AppraisalSkillAreaRating | null>): TcRunSkillAreaRatingItem[] =>
    appraisee.skillAreaFeedback.map((sa) => ({
      skillAreaId: sa.skillAreaId,
      updatedRating: ratings[sa.skillAreaId] ?? null,
    }));

  const mutateRatings = (
    ratings: Record<number, AppraisalSkillAreaRating | null>,
    finalRating: AppraisalSkillAreaRating | null,
    promote: boolean,
    holdAtGrade: boolean
  ) => {
    // Goes through the page-level queue so it's ordered against any sibling-section
    // mutation on the same appraisal rowVersion (Notes for Collator / Confidential Notes).
    void runSerialized((rowVersion) =>
      updateRatingsMutation.mutateAsync({
        tcId,
        appraisalId: appraisee.appraisalId,
        data: {
          skillAreaRatings: buildSkillAreaRatings(ratings),
          finalRating,
          promote,
          holdAtGrade,
        },
        rowVersion,
      })
    ).catch(() => {
      /* 409 surfaced by the global Axios interceptor */
    });
  };

  const handleUpdatedRatingChange = (skillAreaId: number, event: SelectChangeEvent<string>) => {
    const newValue = event.target.value === '' ? null : (event.target.value as AppraisalSkillAreaRating);
    const newRatings = { ...localRatings, [skillAreaId]: newValue };
    setLocalRatings(newRatings);
    mutateRatings(newRatings, localFinalRating, localPromote, localHoldAtGrade);
  };

  const handleFinalRatingChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value === '' ? null : (event.target.value as AppraisalSkillAreaRating);
    setLocalFinalRating(newValue);
    mutateRatings(localRatings, newValue, localPromote, localHoldAtGrade);
  };

  const handlePromoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    const nextHoldAtGrade = newValue ? false : localHoldAtGrade;
    setLocalPromote(newValue);
    setLocalHoldAtGrade(nextHoldAtGrade);
    mutateRatings(localRatings, localFinalRating, newValue, nextHoldAtGrade);
  };

  const handleHoldAtGradeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    const nextPromote = newValue ? false : localPromote;
    setLocalHoldAtGrade(newValue);
    setLocalPromote(nextPromote);
    mutateRatings(localRatings, localFinalRating, nextPromote, newValue);
  };

  const sortedFeedback = [...appraisee.skillAreaFeedback].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Section>
      <SectionHeader>RATINGS AND ACTIONS</SectionHeader>
      <SectionCard>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Skill Area</TableCell>
                <TableCell>Collator Rating</TableCell>
                <TableCell>Updated Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFeedback.map((sa) => (
                <TableRow key={sa.skillAreaId}>
                  <TableCell>{sa.skillAreaName}</TableCell>
                  <TableCell>{sa.rating ?? '-'}</TableCell>
                  <TableCell>
                    {readOnly ? (
                      <Typography variant="body2">{localRatings[sa.skillAreaId] ?? '-'}</Typography>
                    ) : (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={localRatings[sa.skillAreaId] ?? ''}
                          onChange={(e) => handleUpdatedRatingChange(sa.skillAreaId, e)}
                          displayEmpty
                          aria-label={`Updated Rating for ${sa.skillAreaName}`}
                          renderValue={(selected) => selected || '-'}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {RATING_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 6, mt: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Final Rating
            </Typography>
            {readOnly ? (
              <Typography variant="body1">{localFinalRating ?? '-'}</Typography>
            ) : (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={localFinalRating ?? ''}
                  onChange={handleFinalRatingChange}
                  displayEmpty
                  aria-label="Final Rating"
                  renderValue={(selected) => selected || 'Select'}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {RATING_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              TC Decision
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localPromote}
                    onChange={handlePromoteChange}
                    disabled={readOnly}
                    inputProps={{ 'aria-label': 'Promote' }}
                  />
                }
                label="Promote"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localHoldAtGrade}
                    onChange={handleHoldAtGradeChange}
                    disabled={readOnly}
                    inputProps={{ 'aria-label': 'Hold at Grade' }}
                  />
                }
                label="Hold at Grade"
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Collator Decision
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>
                  Proposed Rating:
                </Box>
                <Box component="span" sx={{ fontWeight: 600 }}>{appraisee.proposedRating ?? '-'}</Box>
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={appraisee.collatorPromote}
                    disabled
                    inputProps={{ 'aria-label': 'Collator Promote' }}
                  />
                }
                label="Promote"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={appraisee.collatorPerformanceManagementIntervention}
                    disabled
                    inputProps={{ 'aria-label': 'Collator Performance Management Intervention Required' }}
                  />
                }
                label="Performance Management Intervention Required"
              />
            </Box>
          </Box>
        </Box>
      </SectionCard>
    </Section>
  );
}
